import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LOGIN } from "./RoutesUrl";
import { PostDetailContext, NavigationContext } from "../App";
import PostDetailModal from "../components/common/PostDetailModal";
import { SplashApi } from "./API";

export const CountContext = createContext();

const ProtectedRoute = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { postDetail, isOpen, postModalClose } = useContext(PostDetailContext);
  const { updateCurrentUrl } = useContext(NavigationContext);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [count, setCount] = useState(null);
  const [isCheckingSpecialization, setIsCheckingSpecialization] = useState(true);

  const updateCount = (val) => setCount(val);

  const isAllowedPage = (path) => {
    const allowedPaths = [
      '/profile-', '/profile/', '/chat', '/setting', '/notifications',
      '/courses', '/course-detail', '/course-details', '/course-chapter-details',
      '/chapter-quiz', '/course-community', '/course-community-list',
      '/events', '/event-details', '/search', '/post/', '/webinar/',
      '/clinical/', '/clinical-', '/poll/', '/onboarding', '/feed',
      '/waiting', '/again-verification', '/account-reject', '/jobs', '/job-detail', '/my-reports',
      '/live-webinars', '/add-clinical', '/profile-job', '/profile-events',
      '/profile-posts', '/group-setting', '/contests', '/contest-list',
      '/contest-detail', '/new-contest', '/clinical-sessions', '/course/',
      '/event/', '/courses/all', '/courses/filter', '/events/all',
      '/paypal-payment', '/paypal-callback', '/manage-content',
      '/new-course-section', '/new-course-media', '/keratopigmentation',
      '/manage-doctor-profile', '/manage-facility-profile', '/manage-industry-profile',
      '/new-login', '/new-signup', '/upload-content', '/edit_profile',
      '/forgot-password', '/change-password', '/otp-verification', '/wallet',
      '/search/doctors', '/search/posts', '/search/webinars',
      '/search/clinicals', '/search/courses', '/search/events', '/stripe-payment',
      '/mentorship',
    ];
    return allowedPaths.some((p) => path.startsWith(p) || path === p);
  };

  const applyVerificationRouting = (json, currentPath) => {
    if (!json?.status || !json?.user) return;

    const { verified, verification_rejection_reason, user_type,
            doctor_profile, facility_profile, industry_profile } = json.user;

    if (verified === 2) {
      if (user_type === "facility" || user_type === "industry") {
        if (currentPath !== "/account-reject") navigate("/account-reject", { replace: true });
      } else {
        if (currentPath !== "/again-verification") {
          sessionStorage.setItem("verificationRejectionReason", verification_rejection_reason || "");
          navigate("/again-verification", { replace: true });
        }
      }
      return;
    }

    if ((currentPath === "/again-verification" || currentPath === "/account-reject") && verified === 1) {
      if (user_type === "doctor") navigate(doctor_profile ? "/feed" : "/onboarding-doctor", { replace: true });
      else if (user_type === "facility") navigate(facility_profile ? "/feed" : "/onboarding-facility", { replace: true });
      else if (user_type === "industry") navigate(industry_profile ? "/feed" : "/onboarding-industry", { replace: true });
      return;
    }

    if (verified === 0) {
      if (user_type === "doctor") {
        if (currentPath !== "/waiting") navigate("/waiting", { replace: true });
        return;
      }
      if (user_type === "facility") {
        if (!facility_profile && currentPath !== "/onboarding-facility") { navigate("/onboarding-facility", { replace: true }); return; }
        if (facility_profile && currentPath !== "/waiting") { navigate("/waiting", { replace: true }); return; }
        return;
      }
      if (user_type === "industry") {
        if (!industry_profile && currentPath !== "/onboarding-Industry") { navigate("/onboarding-Industry", { replace: true }); return; }
        if (industry_profile && currentPath !== "/waiting") { navigate("/waiting", { replace: true }); return; }
        return;
      }
      return;
    }

    if (verified === 1) {
      if (user_type === "doctor" && !doctor_profile && currentPath !== "/onboarding-doctor") {
        navigate("/onboarding-doctor", { replace: true });
        return;
      }
      if (user_type === "facility" && !facility_profile && currentPath !== "/onboarding-facility") {
        navigate("/onboarding-facility", { replace: true });
        return;
      }
      if (user_type === "industry" && !industry_profile && currentPath !== "/onboarding-industry") {
        navigate("/onboarding-industry", { replace: true });
        return;
      }

      const profileComplete =
        (user_type === "doctor" && doctor_profile) ||
        (user_type === "facility" && facility_profile) ||
        (user_type === "industry" && industry_profile);

      if (profileComplete) {
        const pendingScreens = ["/waiting", "/again-verification", "/account-reject"];
        if (pendingScreens.includes(currentPath) || !isAllowedPage(currentPath)) {
          navigate("/feed", { replace: true });
        }
      }
    }
  };

  const checkVerificationStatus = async () => {
    try {
      const signupData = JSON.parse(localStorage.getItem("SignupData"));
      const token = signupData?.data?.token;
      if (!token) return;
      const response = await fetch(SplashApi, { headers: { Authorization: `Bearer ${token}` } });
      const json = await response.json();
      applyVerificationRouting(json, location.pathname);
    } catch (error) {
      console.error("Verification status check failed:", error);
    }
  };

  const checkUser = () => {
    const userToken = JSON.parse(localStorage.getItem("SignupData"));
    if (!userToken?.data?.id) {
      setIsLoggedIn(false);
      setIsCheckingSpecialization(false);
      return navigate(LOGIN);
    }
    setIsLoggedIn(true);
    setIsCheckingSpecialization(false);
  };

  useEffect(() => {
    updateCurrentUrl(location.pathname);
  }, [location.pathname, updateCurrentUrl]);

  useEffect(() => {
    document.body.style.backgroundColor = isLoggedIn ? "#f0f7fa" : "#1dc1dd";
    return () => { document.body.style.backgroundColor = "#1dc1dd"; };
  }, [isLoggedIn]);

  // Initial auth check
  useEffect(() => {
    checkUser();
  }, []);

  // On route change, fetch fresh verification status
  // Skip initial mount (App.jsx handles that via splash API)
  // Skip onboarding/waiting paths to prevent loops
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => { setIsMounted(true); }, []);

  useEffect(() => {
    if (!isLoggedIn || !isMounted) return;
    const skipPaths = ["/onboarding-doctor", "/onboarding-facility", "/onboarding-Industry", "/waiting"];
    if (skipPaths.includes(location.pathname)) return;
    checkVerificationStatus();
  }, [isLoggedIn, location.pathname]);

  // Poll verification status on pending screens so re-verified users get redirected
  useEffect(() => {
    if (!isLoggedIn) return;
    const pendingScreens = ["/waiting", "/again-verification", "/account-reject"];
    if (!pendingScreens.includes(location.pathname)) return;
    const poll = async () => {
      try {
        const signupData = JSON.parse(localStorage.getItem("SignupData"));
        const token = signupData?.data?.token;
        if (!token) return;
        const response = await fetch(SplashApi, { headers: { Authorization: `Bearer ${token}` } });
        const json = await response.json();
        if (!json?.status || !json?.user) return;
        const { verified, verification_rejection_reason, user_type,
                doctor_profile, facility_profile, industry_profile } = json.user;
        // Only act if verified status has changed away from 0
        if (verified === 0) return;
        applyVerificationRouting(json, location.pathname);
      } catch {}
    };
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, [isLoggedIn, location.pathname]);

if (isCheckingSpecialization) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", backgroundColor: "#f0f7fa" }} />
    );
  }

  return (
    <>
      {isLoggedIn ? (
        <CountContext.Provider value={{ count, updateCount }}>
          {isOpen && (
            <PostDetailModal isOpen={isOpen} onClose={postModalClose} post={postDetail} />
          )}
          {props.children}
        </CountContext.Provider>
      ) : null}
    </>
  );
};

export default ProtectedRoute;
